// El sonido de lo que se abre. A2.4, y son ocho segundos que suenan una vez al mes.
//
// QUÉ TIENE QUE DECIR, porque un sonido de celebración cualquiera no vale. Abrir un sitio es
// **la única recompensa que no se puede comprar** —está escrito así en el mes de la conversión—
// y es la que el alumno se ha ganado estudiando. Así que no es la fanfarria de ganar una
// partida ni el «nivel superado» de un juego: es una **puerta de piedra que cede**.
//
// TRES COSAS Y EN ESTE ORDEN, que es el orden de una puerta que se abre de verdad:
//
//  1. **El cerrojo.** Un golpe seco y grave, con cuerpo de piedra. Es el instante: lo que estaba
//     cerrado deja de estarlo, y pasa antes que la celebración porque la causa va antes que la
//     consecuencia. Sin esto el sonido empieza en el aire y no se sabe de dónde sale.
//  2. **El arrastre.** Ruido filtrado que se abre de grave a agudo en poco menos de un segundo:
//     la hoja moviéndose. Es la parte que dice «esto es una puerta» y no una campana.
//  3. **El acorde.** Cuarta y quinta justas sobre la fundamental —sin tercera— y una octava
//     arriba, sostenidas y entrando tarde. Sin tercera porque una tercera decide si la noticia
//     es alegre o triste, y esto no es ninguna de las dos: **es grande**. Son las mismas
//     quintas sin tercera con las que suena la Explanada, que es desde donde se oye.
//
// Y NO ES UN CANAL NUEVO: va por el informativo, como el umbral del Ojo y la nave. Informa de
// algo que ha pasado en el mundo, no responde a un botón ni es parte de una partida.
//
// POR QUÉ ESTÁ AQUÍ Y NO EN `sound.js`. Porque aquel fichero vive pegado a su límite de
// cuatrocientas líneas y esto son cuarenta de síntesis; es la misma mudanza que ya hicieron el
// despegue y la brasa. Allí queda la línea que lo llama, que es lo que hay que poder encontrar.

/** La fundamental: Sol2. Grave, pero no tanto que se pierda en un portátil de aula. */
export const FUNDAMENTAL = 98.0;

/** Cuarta y quinta justas, y la octava. Ni una tercera: esto no es alegre, es grande. */
export const ACORDE = [1, 4 / 3, 3 / 2, 2];

/** Cuánto dura entero, en segundos. Corto a propósito: «se ve desde donde estés, y dura poco». */
export const DURA = 2.6;

/**
 * Lo toca con el motor de `lib/sound.js`, que es quien tiene el contexto y el interruptor.
 * Recibe el propio `sound` para no abrir un segundo `AudioContext`: dos contextos en una
 * pestaña son dos relojes, y el segundo suele quedarse suspendido sin que nadie se entere.
 */
export function ceremoniaDeApertura(sound) {
  const ctx = sound._ensure();
  if (!ctx) return false;
  const t0 = ctx.currentTime;

  // 1 · EL CERROJO. Seno que cae de 190 a 70 en nada, con un chasquido de ruido encima: el
  // seno es el peso y el ruido es la piedra. Sólo el seno sonaría a bombo.
  sound._tone({ freq: 190, to: 70, type: "sine", dur: 0.22, gain: 0.18 });
  sound._noise(0.09, 900, 0.09, "lowpass");

  // 2 · EL ARRASTRE. Ruido por un paso de banda que sube de 300 a 2600 mientras se apaga. Sube
  // y no baja: una puerta que se abre deja ver más, y en sonido «más» es más agudo.
  const dur = 0.9;
  const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * dur), ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < d.length; i += 1) d[i] = Math.random() * 2 - 1;
  const src = ctx.createBufferSource();
  src.buffer = buf;
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(300, t0 + 0.1);
  bp.frequency.exponentialRampToValueAtTime(2600, t0 + 0.1 + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t0 + 0.1);
  g.gain.exponentialRampToValueAtTime(0.1, t0 + 0.3);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.1 + dur);
  src.connect(bp).connect(g).connect(ctx.destination);
  src.start(t0 + 0.1);
  src.stop(t0 + 0.1 + dur + 0.1);

  // 3 · EL ACORDE, entrando tarde y escalonado. Las voces no arrancan a la vez porque un
  // acorde que entra de golpe es un golpe; una tras otra, se oye abrirse.
  ACORDE.forEach((r, i) => sound._tone({
    freq: FUNDAMENTAL * r,
    type: i === ACORDE.length - 1 ? "triangle" : "sine",
    dur: DURA - 0.7 - i * 0.08,
    gain: i === 0 ? 0.135 : 0.08,
    when: 0.45 + i * 0.09,
  }));
  return true;
}

export default ceremoniaDeApertura;
